/**
 * Ponto de entrada — a sessao da crianca.
 *
 * Fluxo de uma sessao:
 *   amanhecer -> Progression escolhe as tarefas -> a ancora da vez brilha ->
 *   a crianca toca -> a camera foca -> o motor roda -> o efeito muda a fazenda ->
 *   proxima tarefa -> anoitecer natural (D12)
 *
 * D7: nao ha cronometro na tela, nem vidas, nem game over.
 * D9: o botao de casa fica sempre no mesmo canto e sai de qualquer tarefa.
 * D10: nao ha pontuacao. A recompensa e a fazenda mudando.
 */

import * as THREE from 'three';

import { ContentLoader } from './core/ContentLoader.js';
import { SceneManager } from './core/SceneManager.js';
import { InputManager } from './core/InputManager.js';
import { AudioManager } from './core/AudioManager.js';
import { SaveManager } from './core/SaveManager.js';
import { Progression } from './core/Progression.js';
import { obterMotor } from './core/Registry.js';
import { resolverPalpitesVencidos } from './engines/PalpitarEngine.js';

import { Fazenda } from './world/Fazenda.js';
import { CicloDia } from './world/CicloDia.js';
import { Estacoes } from './world/Estacoes.js';
import { aplicarEfeito } from './world/Efeitos.js';
import { clonar } from './world/Modelos.js';

import { BotaoGrande, criarBotaoCasa } from './ui/BotaoGrande.js';
import { Instrucao } from './ui/Instrucao.js';
import { PortaoPais } from './ui/PortaoPais.js';
import { Caderno } from './ui/Caderno.js';

// D14 — perfis separados por crianca, nunca progresso compartilhado.
// O seletor de perfil entra junto do modo pais; por enquanto, um perfil fixo.
const PERFIL_PADRAO = 'perfil-1';

class Jogo {
  constructor(canvas) {
    this.canvas = canvas;
    this.sceneManager = new SceneManager(canvas);
    this.input = new InputManager(canvas, this.sceneManager);
    this.audio = new AudioManager();
    this.save = new SaveManager();
    this.instrucao = new Instrucao(this.audio);

    this.motorAtual = null;
    this.tarefaAtual = null;
    this.tarefasDaSessao = [];
    this.indiceTarefa = 0;
    this.emTarefa = false;
    this.pulsando = null;

    // Conta quantas vezes a crianca saiu de uma tarefa. Serve para descartar
    // uma abertura que ficou pelo caminho — ver abrirTarefa().
    this.geracao = 0;

    this.relogio = new THREE.Clock();
  }

  async iniciar() {
    this.conteudo = new ContentLoader().carregar();
    this.progression = new Progression(this.conteudo);
    this.caderno = new Caderno(this.conteudo);

    // Os modelos .glb precisam estar na memoria antes de montar o cenario.
    const tela = this.mostrarCarregando();
    await Fazenda.carregarModelos((prontos, total) => {
      tela.atualizar(prontos, total);
    });
    tela.remover();

    await this.save.abrir();
    this.estado = await this.save.carregar(PERFIL_PADRAO);

    // O motor `palpitar` e o unico com estado que atravessa sessoes: os
    // palpites cujo prazo venceu passam a poder ser vistos no caderno.
    if (resolverPalpitesVencidos(this.estado) > 0) await this.save.salvar();

    this.fazenda = new Fazenda(this.sceneManager).construir(this.estado);
    this.cicloDia = new CicloDia(this.sceneManager.cena);
    this.estacoes = new Estacoes(this.fazenda);
    this.estacoes.aplicar(this.estado.estacao, false);

    this.cicloDia.aoAnoitecer = () => this.encerrarSessao();

    this.montarInterface();
    this.fazenda.conferirOrcamento();

    // O audio do navegador so libera depois de um gesto.
    this.canvas.addEventListener('pointerdown', () => this.audio.destravar(), { once: true });

    this.save.iniciarSyncAutomatico();

    this.tarefasDaSessao = this.progression.selecionarSessao(this.estado);
    console.info(
      `FAZENDA: sessao de hoje — ${this.tarefasDaSessao.length} tarefa(s): ` +
        this.tarefasDaSessao.map((t) => t.id).join(', ')
    );

    this.sceneManager.renderizador.setAnimationLoop(() => this.loop());
    this.proximaTarefa();
  }

  /**
   * Tela de espera enquanto os modelos carregam.
   * Sem barra de porcentagem em numero: quem espera tem 5 anos. E uma barra
   * que enche e um sol girando.
   */
  mostrarCarregando() {
    const fundo = document.createElement('div');
    fundo.style.cssText = `
      position:fixed; inset:0; z-index:200; display:flex;
      flex-direction:column; align-items:center; justify-content:center; gap:28px;
      background:linear-gradient(#8ed0e8,#c8e6a0);
      font-family:system-ui,-apple-system,sans-serif;`;

    const sol = document.createElement('div');
    sol.textContent = '\u{1F31E}';
    sol.style.cssText = 'font-size:96px; animation:girar 3s linear infinite;';

    const trilho = document.createElement('div');
    trilho.style.cssText =
      'width:min(60vw,420px); height:26px; border-radius:13px; background:rgba(255,255,255,.55); overflow:hidden;';

    const barra = document.createElement('div');
    barra.style.cssText = 'height:100%; width:0%; background:#5a9e2f; transition:width 180ms ease;';
    trilho.appendChild(barra);

    const estilo = document.createElement('style');
    estilo.textContent = '@keyframes girar { to { transform: rotate(360deg) } }';
    document.head.appendChild(estilo);

    fundo.append(sol, trilho);
    document.body.appendChild(fundo);

    return {
      atualizar: (prontos, total) => {
        barra.style.width = `${Math.round((prontos / total) * 100)}%`;
      },
      remover: () => {
        fundo.style.transition = 'opacity 400ms ease';
        fundo.style.opacity = '0';
        setTimeout(() => fundo.remove(), 420);
      },
    };
  }

  montarInterface() {
    // D9 — botao de casa, sempre no mesmo canto, sai de qualquer tarefa.
    this.botaoCasa = criarBotaoCasa(() => this.voltarParaFazenda());

    // O caderno de palpites fica sempre a mao, ao lado do botao de casa.
    this.botaoCaderno = new BotaoGrande({
      simbolo: '\u{1F4D2}',
      rotulo: 'Abrir o caderno de palpites',
      aoTocar: () => this.caderno.abrir(this.estado, () => {}),
    });
    this.botaoCaderno.elemento.style.top = '24px';
    this.botaoCaderno.elemento.style.left = '144px';

    this.portaoPais = new PortaoPais(() => {
      console.info('FAZENDA: modo pais aberto. O painel entra na Fase 6.');
      this.instrucao.mostrarTexto('Area dos pais — painel na Fase 6.');
    });
  }

  // -------------------------------------------------------------------------
  // Ciclo das tarefas
  // -------------------------------------------------------------------------

  proximaTarefa() {
    this.limparPulso();

    if (this.indiceTarefa >= this.tarefasDaSessao.length) {
      // Trabalho do dia terminado: a fazenda vai dormir naturalmente (D12).
      this.cicloDia.apressarAnoitecer();
      this.instrucao.mostrarTexto('A fazenda esta cuidada. O sol esta se pondo...');
      return;
    }

    const tarefa = this.tarefasDaSessao[this.indiceTarefa];
    const ancora = this.fazenda.ancoras.get(tarefa.ancora);

    if (!ancora) {
      console.error(
        `FAZENDA: a tarefa "${tarefa.id}" pede a ancora "${tarefa.ancora}", que nao esta no ` +
          'cenario. Ou a construcao ainda nao foi liberada, ou o campo "ancora" esta errado.'
      );
      this.indiceTarefa += 1;
      this.proximaTarefa();
      return;
    }

    // A ancora da vez brilha e espera o toque. Nada de seta piscando ou texto
    // mandando: o brilho e o convite.
    this.pulsar(ancora);
    this.input.registrar(ancora, () => this.abrirTarefa(tarefa, ancora));
  }

  async abrirTarefa(tarefa, ancora) {
    if (this.emTarefa) return;
    this.emTarefa = true;

    // Guarda em que "geracao" esta abertura comecou. Se a crianca apertar o
    // botao de casa enquanto a narracao toca, a geracao muda e esta abertura
    // e descartada no meio do caminho.
    //
    // Sem isto: a narracao termina depois da saida, o motor e montado assim
    // mesmo, mas emTarefa ja voltou a false — e o loop() nunca conclui a
    // tarefa. A crianca planta tudo e nada acontece.
    //
    // Hoje a janela e de zero milissegundo, porque sem audio a narracao volta
    // na hora. Ela abre de verdade quando os audios existirem: 3 a 5 segundos,
    // que e exatamente quando uma crianca de 5 anos aperta o botao grande.
    const minhaGeracao = this.geracao;

    this.input.desregistrar(ancora);
    this.limparPulso();

    // D1 — o zoom e do codigo, nunca da crianca.
    this.sceneManager.focarEm(this.fazenda.posicaoDaAncora(tarefa.ancora));

    const banda = this.progression.bandaDaTarefa(tarefa, this.estado);
    const idNarracao = tarefa.narracao[String(banda)];
    const narracao = this.conteudo.narracaoPorId(idNarracao);

    // D5 — audio + demonstracao visual, texto junto.
    await this.instrucao.mostrar(idNarracao, narracao);

    if (minhaGeracao !== this.geracao) return; // saiu durante a narracao

    const Motor = obterMotor(tarefa.motor);
    this.motorAtual = new Motor(this.contextoDoMotor());

    try {
      await this.motorAtual.iniciar(tarefa, banda);

      if (minhaGeracao !== this.geracao) {
        // Saiu enquanto o motor montava. Desfaz para nao deixar peca solta.
        this.motorAtual.descartar();
        this.motorAtual = null;
        return;
      }
    } catch (e) {
      // P5 — erro visivel, nunca engolido.
      console.error(e.message);
      this.instrucao.mostrarTexto('Essa tarefa ainda esta sendo construida.');
      this.emTarefa = false;
      this.indiceTarefa += 1;
      setTimeout(() => this.voltarParaFazenda(), 1500);
      return;
    }

    this.tarefaAtual = tarefa;
  }

  contextoDoMotor() {
    return {
      cena: this.sceneManager.cena,
      sceneManager: this.sceneManager,
      fazenda: this.fazenda,
      audio: this.audio,
      input: this.input,
      instrucao: this.instrucao,
      estado: this.estado,
      conteudo: this.conteudo,
      caderno: this.caderno,
    };
  }

  async concluirTarefa() {
    const tarefa = this.tarefaAtual;
    const resultado = await this.motorAtual.concluir();

    // D11 — toda tarefa muda a fazenda.
    aplicarEfeito(tarefa.sucesso.mundo, this.estado, this.fazenda, resultado);

    if (tarefa.sucesso.colecionavel) {
      this.estado.colecao.push(tarefa.sucesso.colecionavel);
    }

    this.progression.registrarCooldown(tarefa, this.estado);
    await this.save.salvar();
    await this.save.enfileirarEvento({ ...resultado, ocorreu_em: new Date().toISOString() });

    this.motorAtual.descartar();
    this.motorAtual = null;
    this.tarefaAtual = null;
    this.emTarefa = false;
    this.indiceTarefa += 1;

    this.instrucao.esconder();
    this.sceneManager.voltarParaFazenda();

    // Uma pausa para a crianca ver a fazenda mudar antes da proxima tarefa.
    setTimeout(() => this.proximaTarefa(), 1600);
  }

  /** D9 — sai de qualquer tarefa a qualquer momento, sem perder nada. */
  voltarParaFazenda() {
    // Invalida qualquer abertura de tarefa que ainda esteja a caminho.
    this.geracao += 1;

    if (this.motorAtual) {
      this.motorAtual.descartar();
      this.motorAtual = null;
      this.tarefaAtual = null;
    }
    this.emTarefa = false;
    this.instrucao.esconder();
    this.audio.pararNarracao();
    this.sceneManager.voltarParaFazenda();
    this.proximaTarefa();
  }

  async encerrarSessao() {
    this.instrucao.mostrarTexto('Ate amanha! A fazenda vai dormir.');

    const { mudouEstacao, novaEstacao } = this.progression.fecharSessao(this.estado);
    if (mudouEstacao) this.estacoes.aplicar(novaEstacao);

    await this.save.salvar();
    await this.save.sincronizar();
  }

  // -------------------------------------------------------------------------
  // Brilho da ancora da vez
  // -------------------------------------------------------------------------

  /**
   * Marca a ancora da vez com um ponteiro flutuante.
   *
   * Antes a ancora so crescia e encolhia 5%, e o responsavel relatou que nao
   * dava para saber onde tocar. Um ponteiro em cima do objeto e uma seta, nao
   * uma sugestao — e a crianca nao le, entao tem que ser visual.
   */
  pulsar(objeto) {
    this.limparPulso();

    const caixa = new THREE.Box3().setFromObject(objeto);
    const topo = caixa.max.y;
    const centro = caixa.getCenter(new THREE.Vector3());

    const ponteiro = clonar('ponteiro');
    ponteiro.scale.setScalar(2.4);
    ponteiro.position.set(centro.x, topo + 1.6, centro.z);
    this.sceneManager.adicionar(ponteiro);

    this.pulsando = { objeto, ponteiro, t: 0, alturaBase: topo + 1.6 };
  }

  limparPulso() {
    if (!this.pulsando) return;
    this.sceneManager.remover(this.pulsando.ponteiro);
    this.pulsando = null;
  }

  loop() {
    const dt = Math.min(this.relogio.getDelta(), 0.1);

    this.sceneManager.atualizar(dt);
    this.cicloDia.atualizar(dt);
    this.estacoes.atualizar(dt);

    this.fazenda.atualizar(dt);

    if (this.pulsando) {
      // O ponteiro sobe e desce e gira devagar: chama o olho sem assustar.
      this.pulsando.t += dt;
      this.pulsando.ponteiro.position.y =
        this.pulsando.alturaBase + Math.sin(this.pulsando.t * 2.2) * 0.5;
      this.pulsando.ponteiro.rotation.y += dt * 1.2;
    }

    if (this.motorAtual) {
      this.motorAtual.atualizar(dt);
      if (this.motorAtual.concluiu?.() && this.emTarefa && this.tarefaAtual) {
        this.emTarefa = false; // trava para nao concluir duas vezes
        this.concluirTarefa();
      }
    }

    this.sceneManager.renderizar();
  }
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

const canvas = document.getElementById('tela');

if (!canvas) {
  throw new Error(
    'FAZENDA: nao encontrei o <canvas id="tela"> no index.html. ' +
      'Sem ele nao ha onde desenhar o jogo.'
  );
}

const jogo = new Jogo(canvas);
jogo.iniciar().catch((e) => {
  // P5 — a falha aparece inteira no console, em portugues, com a pilha.
  console.error('FAZENDA: o jogo nao conseguiu iniciar.');
  console.error(e);
});
